import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { VehicleHistory } from './models/vehicle-history.model';
import { SidenavService } from '../../services/sidenav.service';
import { AppState } from '../../../store/state.model';
import { transformDateTime } from '../../../common/helpers/utils';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { MatButton, MatAnchor } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatPaginator as MatPaginator_1 } from '@angular/material/paginator';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort as MatSort_1, MatSortHeader } from '@angular/material/sort';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-vehicle-history',
    templateUrl: './vehicle-history.component.html',
    styleUrls: ['./vehicle-history.component.scss'],
    imports: [MatButton, NgIf, MatFormField, MatLabel, MatInput, MatPaginator_1, MatTable, MatSort_1, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatAnchor, MatIcon, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslatePipe]
})
export class VehicleHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * @Property to observe the redux api store
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  
  /**
   * @Property to store the api subscription
   **/
  private apiSubscription: Subscription;
  /**
  * @Property to show or hide the table
  **/
  public showTable = false;
  /**
   * @Property to hold the displayed columns
   **/
  public displayedColumns: string[];
  /**
  * @Property to hold the data source
  **/
  public dataSource: MatTableDataSource<VehicleHistory>;
  /**
   * @arg paginator paginator
   */
  public paginator;
  /**
  * @Property to hold the vehicle history
  **/
  public vehicleHistory: any;
  /**
  * @Property to hold the details of the row
  **/
  public vehicleRowDetails: string;
  /**
  * @Property to hold the details of the selected element
  **/
  public selectedElement: object;
  /**
   * @arg content view page element
   */
  @ViewChild(MatPaginator, { static: false } as any) set matpaginator(paginator: ElementRef) {
    this.paginator = paginator;
    if (this.paginator && !!this.dataSource) {
      this.dataSource.paginator = this.paginator;
    }
  }

  @ViewChild(MatSort, { static: false }) sort: MatSort;
  /**
   * @method applyFilter method will take the filterValue and filter over the dataSourse.
   * @param string filterValue anyvalue
   */
  public applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  constructor(
    public orch: OrchestrationService,
    public translate: TranslationService,
    public apiDispatcher: ApiResponseDispatcher,
    private sidenav: SidenavService,
    private ngRedux: NgRedux<AppState>,
  ) { }

  /**
   * @function ngOnInit sets initial configuration
   */
  ngOnInit() {
    // Initialize data source early to set up sorting configuration
    this.dataSource = new MatTableDataSource([]);
    
    // Set up the sortingDataAccessor before any data is loaded
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'eventTimestamp') {
        return item.eventTimestampRaw;
      }
      if (property === 'serialNumber') {
        return item.serialNumber;
      }
      // For all other fields, return the value (handles strings properly)
      return item[property] || '';
    };
    
    // Load initial data from store
    this.loadVehicleHistoryFromStore();
    
    // Subscribe to store changes to update when new vehicle history is fetched
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api && api.vehicleHistory) {
        this.loadVehicleHistoryFromStore();
      }
    });
  }
  
  /**
   * @function loadVehicleHistoryFromStore loads vehicle history data from Redux store
   */
  private loadVehicleHistoryFromStore(): void {
    const vehicleHistoryStoreValue = this.ngRedux.getState().api.vehicleHistory;
    // Normalize the store value to a plain array for the table.
    const historyRecords = Array.isArray(vehicleHistoryStoreValue) ? vehicleHistoryStoreValue : vehicleHistoryStoreValue && vehicleHistoryStoreValue['vehicleEventHistoryData'];
    // Always bind an array (fallback to []), then build the table.
    this.vehicleHistory = historyRecords || [];
    
    this.setTableData(this.vehicleHistory);
  }

  /**
   * @function ngAfterViewInit Runs after view is initialized
   */
  ngAfterViewInit() {
    this.initializeSort();
  }
  
  /**
   * @function ngOnDestroy cleans up subscriptions when component is destroyed
   */
  ngOnDestroy() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
  }

  /**
   * Initializes or re-initializes the sort connection and applies default sort.
   */
  private initializeSort(): void {
    setTimeout(() => {
      if (this.sort && this.dataSource) {
        this.dataSource.sort = this.sort;
        this.sort.disableClear = true;
        
        // Apply default sort to 'serialNumber' column ascending
        this.sort.active = 'serialNumber';
        this.sort.direction = 'asc';
        this.sort.sortChange.emit({ active: 'serialNumber', direction: 'asc' });
      }
    });
  }

  /**
   * @function setTableData set table data
   */
  setTableData(vehicleHistory: Array<any>) {
    const tableSource = this.getTableSource(vehicleHistory);
    
    // Simply assign the data without pre-sorting - let MatSort handle all sorting
    this.dataSource.data = tableSource;
    this.displayedColumns = ['serialNumber', 'eventName', 'imei', 'eventOriginator', 'eventDestination', 'eventType', 'eventStatus', 'eventTimestamp', 'request'];
    
    if (this.dataSource && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    
    this.showTable = this.dataSource.data && this.dataSource.data.length > 0 ? true : false;
    
    // Re-initialize sort after data is loaded
    this.initializeSort();
  }


  /**
   * @function getTableSource get table data
   */
  getTableSource(vehicleHistory: Array<any>): Array<any> {
    if (!vehicleHistory) {
      return [];
    }
    
    // Add raw date for proper sorting and serialNumber for row number sorting
    return vehicleHistory.map((element, index) => ({
      ...element,
      serialNumber: index + 1,
      eventTimestampRaw: element.eventTimestamp ? new Date(element.eventTimestamp) : null
    }));
  }
  /**
  * @function close closes the side nav when called
  **/
  public close(): void {
    this.vehicleHistory.forEach(element => {
      element['isExpanded'] = false;
    });
    this.sidenav.close();
  }

  /**
   * @function viewRowHistory views the details of the row selected
   */
  public viewRowHistory(selectedElement): void {
    selectedElement.isExpanded = !selectedElement.isExpanded;
    this.selectedElement = selectedElement;
    this.vehicleRowDetails = JSON.stringify(selectedElement.request, undefined, 3);
    this.vehicleHistory.forEach(element => {
      if (JSON.stringify(selectedElement) != JSON.stringify(element)) {
        element['isExpanded'] = false;
      }
    });
  }

  /**
   * @function formatTimeStamp formats the datetime
   */
  public formatTimeStamp(timestamp) {
    return transformDateTime(timestamp);
  }

}
